import React, { useEffect, useRef, useState } from 'react'

import { HideableColKey } from './helpers'
import { CheckSquare } from './pills'
import { ColumnsSvg, SearchSvg } from './icons'
import styles from './ColumnsMenu.module.scss'

/**
 * Show/hide-columns dropdown rendered in the header Action <th>. Owns its own
 * open-state, outside-click effect and column-search box; the parent supplies
 * the columns, the hidden-set, and the toggle callbacks.
 */

interface ColumnsMenuProps {
  columns: { key: HideableColKey; label: string }[]
  hiddenCols: Set<HideableColKey>
  onToggleCol: (key: HideableColKey) => void
  onToggleAll: () => void
}

const ColumnsMenu: React.FC<ColumnsMenuProps> = ({
  columns,
  hiddenCols,
  onToggleCol,
  onToggleAll,
}) => {
  const [open, setOpen] = useState(false)
  const [colSearch, setColSearch] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)

  // Close the columns dropdown on outside click.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const colQuery = colSearch.trim().toLowerCase()
  const colDefsFiltered = columns.filter(
    (c) => !colQuery || c.label.toLowerCase().includes(colQuery)
  )

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <button
        type="button"
        title="Show/hide columns"
        className={styles.menuBtn}
        onClick={() => setOpen((v) => !v)}
      >
        <ColumnsSvg size={13} />
      </button>
      {open && (
        <div className={styles.panel}>
          <div className={styles.searchWrap}>
            <SearchSvg size={14} />
            <input
              className={styles.searchInput}
              value={colSearch}
              onChange={(e) => setColSearch(e.target.value)}
              placeholder="Search columns"
            />
          </div>
          <div className={styles.headerRow}>
            <span className={styles.overline}>Columns</span>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={onToggleAll}
            >
              {hiddenCols.size > 0 ? 'Select all' : 'Unselect all'}
            </button>
          </div>
          <div className={styles.list}>
            {colDefsFiltered.map((c) => (
              <button
                key={c.key}
                type="button"
                className={styles.toggleRow}
                onClick={() => onToggleCol(c.key)}
              >
                <CheckSquare on={!hiddenCols.has(c.key)}>
                  {!hiddenCols.has(c.key) ? '✓' : ''}
                </CheckSquare>
                {c.label}
              </button>
            ))}
            {colDefsFiltered.length === 0 && (
              <div className={styles.empty}>No columns found</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ColumnsMenu
